/**
 * EditablePolygon Component
 * Interactive polygon with draggable vertices and edge manipulation
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 1218-1429, 211 lines)
 *
 * Features:
 * - Drag vertices to reshape polygon
 * - Shift+drag for orthogonal constraint (horizontal/vertical only)
 * - Alt+click on edge to add new vertex
 * - Alt+click on vertex to remove vertex (minimum 3 vertices)
 * - Smooth dragging with live updates (no React re-renders during drag)
 * - Visual feedback with colored vertex handles
 * - Dotted outline for editing state
 */

import React, { useState, useEffect, useRef } from 'react';
import { Polyline, Marker } from 'react-leaflet';
import L from 'leaflet';

export interface EditablePolygonProps {
  /** Polygon coordinates [lat, lng][] - must include closing point (first === last) */
  coordinates: [number, number][];

  /** Callback when coordinates change (from drag, add, or remove vertex) */
  onCoordinatesChange: (newCoords: [number, number][]) => void;

  /** Polyline style (color, weight, fillColor, fillOpacity) */
  style: {
    color: string;
    weight: number;
    fillColor: string;
    fillOpacity: number;
  };

  /** Vertex fill color (default: #10b981 green) */
  vertexFillColor?: string;

  /** Vertex border color (default: #2563eb blue) */
  vertexBorderColor?: string;

  /** When true, click on a vertex (without Alt) removes it. Eases discovery for users who don't use shortcuts. */
  removeVertexOnClick?: boolean;

  /** When true, click on a polyline edge (without Alt) adds a vertex. */
  addVertexOnClick?: boolean;
}

/**
 * Interactive polygon editor with vertex manipulation
 *
 * Interaction modes:
 * - Drag vertex: Move corner of polygon
 * - Shift+drag vertex: Constrain to horizontal or vertical axis
 * - Alt+click on edge: Add new vertex at clicked position
 * - Alt+click on vertex: Remove vertex (minimum 3 required)
 *
 * Performance:
 * - Uses refs to avoid React re-renders during drag
 * - Only updates React state on dragend (single re-render)
 * - Direct Leaflet polyline updates for smooth dragging
 */
export function EditablePolygon({
  coordinates,
  onCoordinatesChange,
  style,
  vertexFillColor = '#10b981', // Default green
  vertexBorderColor = '#2563eb', // Default blue
  removeVertexOnClick = false,
  addVertexOnClick = false,
}: EditablePolygonProps) {
  const [draggedVertexIndex, setDraggedVertexIndex] = useState<number | null>(null);
  const polylineRef = useRef<any>(null);
  const tempCoordsRef = useRef<[number, number][]>(coordinates);
  const dragStartPosRef = useRef<[number, number] | null>(null);
  const isShiftKeyRef = useRef<boolean>(false);

  // Update tempCoordsRef when coordinates change from parent
  useEffect(() => {
    tempCoordsRef.current = coordinates;
  }, [coordinates]);

  // Track shift key state
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        isShiftKeyRef.current = true;
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        isShiftKeyRef.current = false;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Create custom circle icon for draggable markers
  const createCircleIcon = (fillColor: string, borderColor: string) => {
    return L.divIcon({
      className: 'custom-circle-marker',
      html: `<div style="
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background-color: ${fillColor};
        border: 3px solid ${borderColor};
        cursor: move;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      "></div>`,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
  };

  const handleVertexDragLive = (index: number, newLatLng: any) => {
    let finalLat = newLatLng.lat;
    let finalLng = newLatLng.lng;

    // Apply orthogonal constraint if Shift is held
    if (isShiftKeyRef.current && dragStartPosRef.current) {
      const startLat = dragStartPosRef.current[0];
      const startLng = dragStartPosRef.current[1];
      const deltaLat = Math.abs(finalLat - startLat);
      const deltaLng = Math.abs(finalLng - startLng);

      // Constrain to the axis with larger movement
      if (deltaLat > deltaLng) {
        // More vertical movement - lock longitude
        finalLng = startLng;
      } else {
        // More horizontal movement - lock latitude
        finalLat = startLat;
      }
    }

    // Update temp coords in ref (no React re-render)
    tempCoordsRef.current = [...tempCoordsRef.current];
    tempCoordsRef.current[index] = [finalLat, finalLng];

    // Update closing point
    if (index === 0) {
      tempCoordsRef.current[tempCoordsRef.current.length - 1] = [finalLat, finalLng];
    } else if (index === tempCoordsRef.current.length - 1) {
      tempCoordsRef.current[0] = [finalLat, finalLng];
    }

    // Update polyline visual directly (smooth, no React re-render)
    if (polylineRef.current) {
      polylineRef.current.setLatLngs(tempCoordsRef.current);
    }
  };

  const handleVertexDragStart = (index: number) => {
    setDraggedVertexIndex(index);
    // Store starting position for orthogonal constraint
    dragStartPosRef.current = coordinates[index];
  };

  const handleVertexDragEnd = () => {
    // Only now update React state (single re-render)
    onCoordinatesChange(tempCoordsRef.current);
    setDraggedVertexIndex(null);
    dragStartPosRef.current = null;
  };

  const addVertex = (index: number, latLng: any) => {
    const newCoords = [...coordinates];
    // Insert new vertex at clicked position
    newCoords.splice(index + 1, 0, [latLng.lat, latLng.lng]);
    tempCoordsRef.current = newCoords;
    onCoordinatesChange(newCoords);
  };

  const removeVertex = (index: number) => {
    if (coordinates.length <= 4) {
      // Don't allow removing if only 3 vertices left (minimum for polygon)
      alert('Cannot remove vertex: minimum 3 vertices required');
      return;
    }
    const newCoords = [...coordinates];
    newCoords.splice(index, 1);
    // Update closing point
    newCoords[newCoords.length - 1] = newCoords[0];
    tempCoordsRef.current = newCoords;
    onCoordinatesChange(newCoords);
  };

  const handlePolylineClick = (e: any) => {
    // Check if Option/Alt key is held, or addVertexOnClick mode is active
    if (e.originalEvent.altKey || addVertexOnClick) {
      const clickedLatLng = e.latlng;

      // Find which edge was clicked (closest edge)
      let closestEdgeIndex = 0;
      let minDistance = Infinity;

      for (let i = 0; i < coordinates.length - 1; i++) {
        const p1 = L.latLng(coordinates[i]);
        const p2 = L.latLng(coordinates[i + 1]);
        const distance = L.LineUtil.pointToSegmentDistance(
          e.layerPoint,
          e.target._map.latLngToLayerPoint(p1),
          e.target._map.latLngToLayerPoint(p2)
        );
        if (distance < minDistance) {
          minDistance = distance;
          closestEdgeIndex = i;
        }
      }

      addVertex(closestEdgeIndex, clickedLatLng);
    }
  };

  return (
    <>
      {/* Polygon outline */}
      <Polyline
        ref={polylineRef}
        positions={coordinates}
        pathOptions={{
          color: style.color,
          weight: style.weight,
          fillColor: style.fillColor,
          fillOpacity: style.fillOpacity,
          dashArray: '5, 5', // Dotted line
        }}
        eventHandlers={{
          click: handlePolylineClick,
        }}
      />

      {/* Vertex markers (draggable) */}
      {coordinates.slice(0, -1).map((coord, index) => (
        <Marker
          key={`vertex-${index}`}
          position={coord}
          // @ts-ignore - react-leaflet v5 supports icon but v2 types don't
          icon={createCircleIcon(vertexFillColor, vertexBorderColor)}
          draggable={true}
          eventHandlers={{
            click: (e: any) => {
              // Alt/Option + click, or click when removeVertexOnClick mode is on
              if (e.originalEvent.altKey || removeVertexOnClick) {
                removeVertex(index);
              }
            },
            dragstart: () => {
              handleVertexDragStart(index);
            },
            drag: (e: any) => {
              const marker = e.target;
              const latLng = marker.getLatLng();
              handleVertexDragLive(index, latLng);
            },
            dragend: () => {
              handleVertexDragEnd();
            },
          }}
        />
      ))}
    </>
  );
}
